import React, { useState, useEffect } from 'react';
import { ExportButton } from './ExportButton';
import {
  FaPen,
  FaEraser,
  FaHighlighter,
  FaEye,
  FaTrash,
  FaUndo,
  FaRedo,
  FaEllipsisH,
  FaTimes,
  FaHandPointer,
  FaCircle,
  FaPlay,
  FaMinus,
  FaArrowRight,
  FaStar,
  FaVectorSquare,
  FaMicrophone,
  FaStop,
  FaList,
  FaSave,
  FaShare,
  FaCog,
  FaShapes,
  FaCube,
  FaChevronLeft,
  FaChevronRight,
  FaVideo,
  FaChevronUp,
  FaChevronDown
} from 'react-icons/fa';
// Voice feedback disabled
// import { useVoiceFeedback } from '../hooks/useVoiceFeedback';
import { BsHexagon, BsPentagon, BsTriangle, BsCircle, BsSquare, BsDiamond } from 'react-icons/bs';
import { TbCylinder, TbPyramid } from 'react-icons/tb';
import { BiGridAlt } from 'react-icons/bi';
import { VscGraphLine } from 'react-icons/vsc';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { Tool, Recording } from '../types';
import { useRecordingInit } from '../hooks/useRecordingInit';
import { useNavigate } from 'react-router-dom';
import { generateDeviceId } from '../utils/deviceInfo';
import { QBTestQuestionEto, QbTestCoursesGroupEto } from '../lib/api/types';
import { SolutionViewerDropdown } from './SolutionViewerDropdown';
import { RecordingService, RecordingMetadata } from '../lib/firebase/recordingService';
import { useUserStore } from '../store/useUserStore';
import { useTenantStore } from '../store/useTenantStore';
import { RecordingPermissions } from '../types/whiteboardProps';
import { getEffectiveRecordingPermissions } from '../utils/recordingPermissionUtils';
import { getGlassClasses, GlassColor } from '../utils/glassStyles';
import { useTheme } from '../hooks/useDarkMode';
import { MobileOpticalForm } from './whiteboard/features/MobileOpticalForm';
import { OpticalAnswer } from '../types';

interface MobileToolbarProps {
  currentQuestionNumber?: number;
  selectedAnswer?: 'A' | 'B' | 'C' | 'D' | 'E' | null;
  onAnswerSelect?: (answer: 'A' | 'B' | 'C' | 'D' | 'E', questionId?: string) => void;
  showOpticalForm?: boolean;
  qbGroups?: QbTestCoursesGroupEto[];
  currentQbQuestion?: QBTestQuestionEto | null;
  opticalAnswers?: { [key: string]: OpticalAnswer | null };
  allowExport?: boolean;
  // New recording permissions system
  recordingPermissions?: RecordingPermissions;
  // Deprecated recording props (kept for backward compatibility)
  showRecordingControls?: boolean;
  allowRecording?: boolean;
  showRecordingsList?: boolean;
  maxRecordingDuration?: number;
  recordingQuality?: 'low' | 'medium' | 'high';
  autoSaveRecording?: boolean;
  onQuestionChange?: (index: number) => void;
  questions?: QBTestQuestionEto[];
  currentIndex?: number;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  onVideoClick?: () => void;
  hasVideo?: boolean;
  // Solution viewing props
  allowSolutionViewing?: boolean;
  solutionVisibilityMode?: 'own' | 'tenant' | 'test' | 'public';
  allowOwnSolutionManagement?: boolean;
  maxSolutionsToShow?: number;
  showSolutionAuthorInfo?: boolean;
  showSolutionStats?: boolean;
  currentTestId?: string;
  // Test mode props
  isTestMode?: boolean;
  totalTime?: number;
  isTestCompleted?: boolean;
  onTestComplete?: () => void;
  // Elimination display
  showEliminationDisplay?: boolean;
  onToggleEliminationDisplay?: () => void;
  hasEliminatedChoices?: boolean;
  // Additional metadata for recording naming
  testTitle?: string;
}

const MobileToolbar: React.FC<MobileToolbarProps> = ({
  onAnswerSelect,
  showOpticalForm: showOpticalFormProp = false,
  qbGroups = [],
  currentQbQuestion,
  opticalAnswers = {},
  onQuestionChange,
  questions = [],
  currentIndex = 0,
  onNavigatePrevious,
  onNavigateNext,
  onVideoClick,
  hasVideo = false,
  allowExport = true,
  recordingPermissions,
  // Deprecated recording props
  showRecordingControls = true,
  allowRecording = true,
  showRecordingsList = true,
  maxRecordingDuration,
  recordingQuality = 'medium',
  autoSaveRecording = false,
  // Solution viewing props
  allowSolutionViewing = false,
  solutionVisibilityMode = 'own',
  allowOwnSolutionManagement = true,
  maxSolutionsToShow = 10,
  showSolutionAuthorInfo = true,
  showSolutionStats = true,
  currentTestId,
  // Test mode props
  isTestMode = false,
  totalTime,
  isTestCompleted = false,
  onTestComplete,
  // Elimination display
  showEliminationDisplay = true,
  onToggleEliminationDisplay,
  hasEliminatedChoices = false,
  // Additional metadata for recording naming
  testTitle
}) => {
  // Get effective recording permissions
  const effectivePermissions = getEffectiveRecordingPermissions(
    recordingPermissions,
    {
      showRecordingControls,
      allowRecording,
      showRecordingsList,
      maxRecordingDuration,
      recordingQuality,
      autoSaveRecording
    }
  );
  
  
  const {
    currentTool,
    setCurrentTool,
    currentColor,
    setCurrentColor,
    currentWidth,
    setCurrentWidth,
    undo,
    redo,
    canUndo,
    canRedo,
    clearCanvas,
    isPenMode,
    setPenMode,
    touchEnabled,
    setTouchEnabled,
    penSettings,
    updatePenSettings,
    shapeRecognitionEnabled,
    setShapeRecognitionEnabled,
    showAnswerFeedback,
    setShowAnswerFeedback,
    recordingSession,
    startRecording,
    stopRecording,
    saveRecording,
    deleteRecording,
    loadDevicePreferences,
    canvasRef,
    saveDevicePreferences,
    updateDevicePreferences,
    questionPositionMetadata,
    glassEffects,
    devicePreferences,
    setGlassEffects
  } = useWhiteboardStore();

  // Debug logging for recording permissions
  if (process.env.NODE_ENV === 'development') {
    // Removed debug logging
  }

  const navigate = useNavigate();
  const recordings = useRecordingInit();
  const { isDark } = useTheme();
  // Voice feedback disabled
  // const { speakToolChange } = useVoiceFeedback();

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showWidthPicker, setShowWidthPicker] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showRecordings, setShowRecordings] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [recordingName, setRecordingName] = useState('');
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const [firebaseRecordings, setFirebaseRecordings] = useState<RecordingMetadata[]>([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const [recordingsCount, setRecordingsCount] = useState(0);
  const [quickColors, setQuickColors] = useState(['#000000', '#FF0000', '#0000FF', '#00FF00']);
  const [showOpticalFormModal, setShowOpticalFormModal] = useState(false);
  const [showQuestionListModal, setShowQuestionListModal] = useState(false);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number>(-1);
  const [showQuestionList, setShowQuestionList] = useState(false);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768);
  const [isToolbarHidden, setIsToolbarHidden] = useState(false);
  const [showTabletNavigation, setShowTabletNavigation] = useState(() => {
    // Load preference from localStorage
    const saved = localStorage.getItem('tabletNavigationVisible');
    return saved !== null ? saved === 'true' : true;
  });
  
  // Check URL parameter for optical mode - URL has priority
  const searchParams = new URLSearchParams(window.location.search);
  const urlOptikParam = searchParams.get('optik');
  const showOpticalForm = urlOptikParam !== null 
    ? urlOptikParam === '1'
    : showOpticalFormProp;
  
  
  // Load recording count when question changes
  useEffect(() => {
    if (currentQbQuestion?.questionId) {
      loadRecordingCount();
    }
  }, [currentQbQuestion?.questionId]);

  // Debug navigation props
  useEffect(() => {
    // Removed debug logging
  }, [questions.length, currentIndex, onNavigatePrevious, onNavigateNext, isLandscape]);
  
  // ESC key handler for modals
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Close any open modal
        if (showShapeMenu) setShowShapeMenu(false);
        else if (showSettings) setShowSettings(false);
        else if (showRecordings) setShowRecordings(false);
        else if (showOpticalFormModal) setShowOpticalFormModal(false);
        else if (showQuestionListModal) setShowQuestionListModal(false);
        else if (showQuestionList) setShowQuestionList(false);
        else if (showColorPicker) setShowColorPicker(false);
        else if (showWidthPicker) setShowWidthPicker(false);
        else if (showMoreMenu) setShowMoreMenu(false);
        else if (showSaveDialog) setShowSaveDialog(false);
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showShapeMenu, showSettings, showRecordings, showOpticalFormModal, showQuestionListModal, showQuestionList,
      showColorPicker, showWidthPicker, showMoreMenu, showSaveDialog]);

  // Helper function to find which group a question belongs to
  const findQuestionGroup = (questionId: string | undefined) => {
    if (!questionId || !qbGroups || qbGroups.length === 0) return -1;
    
    for (let groupIndex = 0; groupIndex < qbGroups.length; groupIndex++) {
      const group = qbGroups[groupIndex];
      if (group.courses) {
        for (const course of group.courses) {
          if (course.questions) {
            for (const question of course.questions) {
              if (question.questionId === questionId) {
                return groupIndex;
              }
            }
          }
        }
      }
    }
    return -1; // Not found, return -1 for "Tümü"
  };

  // Update group index only when question changes (not when manually selected)
  useEffect(() => {
    if (currentQbQuestion) {
      const groupIndex = findQuestionGroup(currentQbQuestion.questionId);
      if (groupIndex !== -1 && groupIndex !== selectedGroupIndex) {
        setSelectedGroupIndex(groupIndex);
      }
    }
  }, [currentQbQuestion?.questionId]);

  // Primary tools
  const primaryTools: Array<{ tool: Tool; icon: React.ReactElement; label: string }> = [
    { tool: 'pen', icon: <FaPen size={20} />, label: 'Kalem' },
    { tool: 'object-eraser', icon: <FaEraser size={20} className="text-red-500" />, label: 'Nesne Sil' },
    { tool: 'highlighter', icon: <FaHighlighter size={20} />, label: 'Vurgulayıcı' },
    { tool: 'select', icon: <FaHandPointer size={20} />, label: 'Seç' },
  ];

  // Extended tools for tablets
  const extendedTools: Array<{ tool: Tool | 'shapes'; icon: React.ReactElement; label: string }> = [
    // { tool: 'eraser', icon: <FaEraser size={20} />, label: 'Silgi' },
    { tool: 'area-eraser', icon: <FaVectorSquare size={20} />, label: 'Bölge Sil' },
    { tool: 'laser', icon: <FaEye size={20} />, label: 'Lazer' },
    { tool: 'shapes', icon: <FaShapes size={20} />, label: 'Şekiller' },
  ];

  // All shape tools organized by category
  const shapeTools: Array<{ tool: Tool; icon: React.ReactElement; label: string; category?: string }> = [
    // Basic 2D Shapes
    { tool: 'circle', icon: <BsCircle size={24} />, label: 'Daire', category: '2D' },
    { tool: 'rectangle', icon: <BsSquare size={24} />, label: 'Dikdörtgen', category: '2D' },
    { tool: 'triangle', icon: <BsTriangle size={24} />, label: 'Üçgen', category: '2D' },
    { tool: 'line', icon: <FaMinus size={24} />, label: 'Çizgi', category: '2D' },
    { tool: 'arrow', icon: <FaArrowRight size={24} />, label: 'Ok', category: '2D' },
    { tool: 'star', icon: <FaStar size={24} />, label: 'Yıldız', category: '2D' },
    { tool: 'pentagon', icon: <BsPentagon size={24} />, label: 'Beşgen', category: '2D' },
    { tool: 'hexagon', icon: <BsHexagon size={24} />, label: 'Altıgen', category: '2D' },
    { tool: 'ellipse', icon: <div className="w-7 h-5 border-2 border-current rounded-full" />, label: 'Elips', category: '2D' },
    { tool: 'diamond', icon: <BsDiamond size={24} />, label: 'Elmas', category: '2D' },
    { tool: 'parallelogram', icon: <div className="w-7 h-5 border-2 border-current transform skew-x-12" />, label: 'Paralelkenar', category: '2D' },
    { tool: 'trapezoid', icon: <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-b-[16px] border-l-transparent border-r-transparent border-b-current" />, label: 'Yamuk', category: '2D' },
    
    // 3D Shapes
    { tool: 'cube', icon: <FaCube size={24} />, label: 'Küp', category: '3D' },
    { tool: 'cylinder', icon: <TbCylinder size={28} />, label: 'Silindir', category: '3D' },
    { tool: 'pyramid', icon: <TbPyramid size={28} />, label: 'Piramit', category: '3D' },
    
    // Special
    { tool: 'coordinate', icon: <VscGraphLine size={28} />, label: 'Koordinat', category: 'Özel' },
    { tool: 'grid', icon: <BiGridAlt size={28} />, label: 'Izgara', category: 'Özel' },
  ];

  const colors = ['#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080'];
  const widths = [1, 2, 3, 4, 6, 8, 12, 16, 20, 24, 32];

  // Load device preferences on mount
  useEffect(() => {
    loadDevicePreferences();
    // Load toolbar hidden state
    const savedToolbarHidden = localStorage.getItem('mobileToolbarHidden');
    if (savedToolbarHidden !== null) {
      setIsToolbarHidden(savedToolbarHidden === 'true');
    }
  }, []);

  // Load saved quick colors
  useEffect(() => {
    const savedQuickColors = localStorage.getItem('quickColors');
    if (savedQuickColors) {
      try {
        setQuickColors(JSON.parse(savedQuickColors));
      } catch (e) {}
    }
  }, []);
  
  // Save tablet navigation preference
  useEffect(() => {
    localStorage.setItem('tabletNavigationVisible', showTabletNavigation.toString());
  }, [showTabletNavigation]);

  // Handle orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
      setIsTablet(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // Handlers
  const handleStartRecording = async () => {
    // Extract questionId from currentQbQuestion if available
    const questionId = currentQbQuestion?.questionId;
    await startRecording(questionId, currentTestId);
  };

  // Generate a smart recording name based on test and question metadata
  const generateRecordingName = () => {
    const parts = [];
    
    // Add test title if available
    if (testTitle) {
      // Truncate long test titles
      const truncatedTitle = testTitle.length > 30 ? testTitle.substring(0, 30) + '...' : testTitle;
      parts.push(truncatedTitle);
    }
    
    // Add group/course info if available
    if (currentQbQuestion?.questionId) {
      const groupIndex = findQuestionGroup(currentQbQuestion.questionId);
      if (groupIndex !== -1 && qbGroups[groupIndex]) {
        const group = qbGroups[groupIndex];
        if (group.groupName) {
          parts.push(group.groupName);
        }
      }
      
      // Add question number
      if (currentIndex !== undefined && questions.length > 0) {
        parts.push(`Soru ${currentIndex + 1}/${questions.length}`);
      }
    }
    
    // Add timestamp
    const now = new Date();
    const timeStr = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    const dateStr = now.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
    parts.push(`${dateStr} ${timeStr}`);
    
    return parts.filter(p => p).join(' - ');
  };

  const handleStopRecording = async () => {
    await stopRecording();
    setShowSaveDialog(true);
    setRecordingName(generateRecordingName());
  };

  const handleSaveRecording = () => {
    if (recordingName.trim()) {
      // Get current question metadata from store
      const currentMetadata = questionPositionMetadata[currentQbQuestion?.questionId || ''];
      
      saveRecording(recordingName.trim(), false, currentMetadata);
      setShowSaveDialog(false);
      setRecordingName('');
    }
  };

  const loadRecordingsForQuestion = async (questionId: string) => {
    try {
      setLoadingRecordings(true);
      const { user } = useUserStore.getState();
      const { currentTenant } = useTenantStore.getState();
      
      const recordings: RecordingMetadata[] = [];
      const recordingIds = new Set<string>();
      
      // Load own recordings if permitted
      if (effectivePermissions.recordingVisibility?.showOwnRecordings && user?.id) {
        const ownRecordings = await RecordingService.getRecordingsByQuestion(
          questionId,
          user.id.toString(),
          50
        );
        ownRecordings.forEach(r => {
          recordings.push(r);
          recordingIds.add(r.id);
        });
      }
      
      // Load public recordings if permitted
      if (effectivePermissions.recordingVisibility?.showPublicRecordings) {
        const publicRecordings = await RecordingService.getPublicRecordingsByQuestion(
          questionId,
          50
        );
        publicRecordings.forEach(r => {
          if (!recordingIds.has(r.id)) {
            recordings.push(r);
            recordingIds.add(r.id);
          }
        });
      }
      
      // Load tenant recordings if permitted
      if (effectivePermissions.recordingVisibility?.showTenantRecordings && currentTenant?.tenantId) {
        const tenantRecordings = await RecordingService.getTenantRecordingsByQuestion(
          questionId,
          currentTenant.tenantId.toString(),
          50
        );
        tenantRecordings.forEach(r => {
          if (!recordingIds.has(r.id)) {
            recordings.push(r);
            recordingIds.add(r.id);
          }
        });
      }
      
      // Sort by creation date (newest first)
      recordings.sort((a, b) => b.createdAt - a.createdAt);
      
      // Update the state with filtered recordings
      setFirebaseRecordings(recordings);
      setRecordingsCount(recordings.length);
    } catch (error) {
      console.error('Error loading recordings:', error);
    } finally {
      setLoadingRecordings(false);
    }
  };

  const handlePlayRecording = (recording: Recording) => {
    navigate(`/player/${recording.id}`);
  };

  // Load recording count for current question
  const loadRecordingCount = async () => {
    const questionId = currentQbQuestion?.questionId;
    if (!questionId) return;
    
    try {
      // Get user info
      const { user } = useUserStore.getState();
      const { currentTenant } = useTenantStore.getState();
      
      // Load all recordings same way as button click to get accurate count
      let recordings: RecordingMetadata[] = [];
      
      // First, get user's own recordings for this question
      if (user?.id) {
        const ownRecordings = await RecordingService.getRecordingsByQuestion(
          questionId,
          user.id.toString(),
          50
        );
        recordings = [...ownRecordings];
      }
      
      // Then, get public recordings for this question
      const publicRecordings = await RecordingService.getPublicRecordingsByQuestion(
        questionId,
        50
      );
      
      // Merge and deduplicate
      const recordingIds = new Set(recordings.map(r => r.id));
      publicRecordings.forEach(r => {
        if (!recordingIds.has(r.id)) {
          recordings.push(r);
        }
      });
      
      // If tenant mode, also get tenant recordings
      if (currentTenant?.tenantId) {
        const tenantRecordings = await RecordingService.getTenantRecordingsByQuestion(
          questionId,
          currentTenant.tenantId.toString(),
          50
        );
        
        tenantRecordings.forEach(r => {
          if (!recordingIds.has(r.id)) {
            recordings.push(r);
          }
        });
      }
      
      setRecordingsCount(recordings.length);
    } catch (error) {
      console.error('Failed to load recording count:', error);
      setRecordingsCount(0);
    }
  };

  const handleShareRecording = (recording: Recording) => {
    const url = `${window.location.origin}/player/${recording.id}`;
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        alert('Paylaşım linki kopyalandı!');
      });
    }
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Preference handlers with save
  const handlePenModeChange = (enabled: boolean) => {
    setPenMode(enabled);
    saveDevicePreferences();
  };

  const handleTouchEnabledChange = (enabled: boolean) => {
    setTouchEnabled(enabled);
    saveDevicePreferences();
  };

  const handlePenSettingsChange = (settings: Partial<typeof penSettings>) => {
    updatePenSettings(settings);
    saveDevicePreferences();
  };

  const handleShapeRecognitionChange = (enabled: boolean) => {
    setShapeRecognitionEnabled(enabled);
    saveDevicePreferences();
  };

  const handleShowAnswerFeedbackChange = (show: boolean) => {
    setShowAnswerFeedback(show);
    saveDevicePreferences();
  };

  const handleGlassEffectsChange = (enabled: boolean) => {
    setGlassEffects(enabled);
    saveDevicePreferences();
  };

  const handleGlassColorChange = async (color: GlassColor) => {
    await updateDevicePreferences({ glassColor: color });
  };

  const handleQuestionFrameMarginsChange = async (margins: { top?: number; bottom?: number; left?: number; right?: number }) => {
    const currentMargins = devicePreferences?.preferences?.questionFrameMargins || {};
    const updatedMargins = { ...currentMargins, ...margins };
    await updateDevicePreferences({ questionFrameMargins: updatedMargins });
  };

  const handleToolChange = (tool: Tool) => {
   
    setCurrentTool(tool);
    // Voice feedback disabled
    // speakToolChange(tool);
    saveDevicePreferences();
    
    // If switching from select tool to a shape tool, ensure pen mode is enabled
    if (currentTool === 'select' && tool !== 'select' && tool !== 'eraser' && tool !== 'pen' && tool !== 'highlighter') {
      // It's a shape tool
      setPenMode(true);
      setTouchEnabled(false);
    }
  };

  const handleColorChange = (color: string) => {
    setCurrentColor(color);
    saveDevicePreferences();
  };

  const handleWidthChange = (width: number) => {
    setCurrentWidth(width);
    saveDevicePreferences();
  };

  // Get current shape tool if active
  const currentShapeTool = shapeTools.find(t => t.tool === currentTool);

  return (
    <>
      {/* Landscape Optical Form - Show on the side */}
      {showOpticalForm && questions.length > 0 && currentQbQuestion && isLandscape && (
        <div className={`fixed z-30 ${
          isTablet 
            ? 'right-8 top-1/2 -translate-y-1/2' 
            : 'right-4 top-1/2 -translate-y-1/2'
        }`}>
          <div className="flex flex-col gap-1 items-center bg-white/20 backdrop-blur-xl rounded-2xl p-2 shadow-2xl border border-white/30 backdrop-saturate-150">
            {/* Question Number */}
            <div className="bg-white/30 backdrop-blur-md rounded-full px-1.5 py-0.5 border border-white/40">
              <span className="text-xs font-bold text-gray-800">{currentIndex + 1}</span>
            </div>
            
            {/* Answer Options */}
            <div className="flex flex-col gap-1">
              {(currentQbQuestion.answerType === 1 ? ['A', 'B', 'C', 'D'] : ['A', 'B', 'C', 'D', 'E']).map(choice => {
                const answer = opticalAnswers[currentQbQuestion.questionId || ''];
                return (
                  <button
                    key={choice}
                    onClick={() => {
                      if (onAnswerSelect) {
                        onAnswerSelect(choice as 'A' | 'B' | 'C' | 'D' | 'E');
                      }
                    }}
                    className={`${isTablet ? 'w-8 h-8' : 'w-7 h-7'} rounded-full text-xs font-semibold transition-all ${
                      answer === choice
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white scale-105 shadow-lg'
                        : glassEffects !== false
                          ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                    }`}
                  >
                    {choice}
                  </button>
                );
              })}
            </div>
            
            {/* All Questions Button */}
            {showOpticalForm && questions.length > 0 ? (
              <button
                onClick={() => setShowOpticalFormModal(true)}
                className={`p-0.5 rounded-full transition-all border relative ${
                  glassEffects !== false
                    ? 'bg-white/30 backdrop-blur-md hover:bg-white/40 border-white/40'
                    : 'bg-gray-100 hover:bg-gray-200 border-gray-200'
                }`}
                title="Tüm Sorular"
              >
                <FaList size={16} className="text-gray-700" />
                {questions.length > 0 && (
                  <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                    {currentIndex + 1}
                  </div>
                )}
              </button>
            ) : questions.length > 0 && (
              <button
                onClick={() => setShowQuestionListModal(true)}
                className={`p-0.5 rounded-full transition-all border relative ${
                  glassEffects !== false
                    ? 'bg-white/30 backdrop-blur-md hover:bg-white/40 border-white/40'
                    : 'bg-gray-100 hover:bg-gray-200 border-gray-200'
                }`}
                title="Soru Listesi"
              >
                <FaList size={16} className="text-gray-700" />
                {questions.length > 0 && (
                  <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                    {currentIndex + 1}
                  </div>
                )}
              </button>
            )}
            
            {/* Video Button */}
            {(() => {
             
              return hasVideo && onVideoClick && (
                <button
                  onClick={onVideoClick}
                  className={`p-1.5 rounded-full transition-all border ${
                    glassEffects !== false
                      ? 'bg-white/30 backdrop-blur-md hover:bg-white/40 border-white/40'
                      : 'bg-gray-100 hover:bg-gray-200 border-gray-200'
                  }`}
                  title="Çözümü İzle"
                >
                  <FaVideo size={16} className="text-gray-700" />
                </button>
              );
            })()}
          </div>
        </div>
      )}

      {/* Combined Toolbar and Optical Form */}
      <div className={`fixed z-40 bottom-0 left-0 right-0 safe-area-pb transition-transform duration-300 ${
        glassEffects !== false 
          ? `${getGlassClasses(devicePreferences?.preferences?.glassColor || 'crystal', isDark)} border-t`
          : 'bg-white border-t border-gray-200'
      } ${
        isToolbarHidden && !isLandscape ? 'translate-y-[calc(100%-112px)]' : 'translate-y-0'
      }`}>
        {/* Optical Form Section - Show when enabled (Portrait only) */}
        {showOpticalForm && questions.length > 0 && currentQbQuestion && !isLandscape && (
          <div className="border-b border-white/30 px-1 py-1 bg-white/10 backdrop-blur-md">
            <div className="flex items-center justify-center gap-1">
              {/* Left Section - Toggle & Question Number */}
              <div className="flex items-center gap-1">
                {/* Toggle Button */}
                <button
                  onClick={() => {
                    const newState = !isToolbarHidden;
                    setIsToolbarHidden(newState);
                    localStorage.setItem('mobileToolbarHidden', newState.toString());
                  }}
                  className={`w-8 h-8 rounded-full transition-all border flex items-center justify-center ${
                    glassEffects !== false
                      ? 'bg-white/30 backdrop-blur-md hover:bg-white/40 border-white/40'
                      : 'bg-gray-100 hover:bg-gray-200 border-gray-200'
                  }`}
                  title={isToolbarHidden ? "Araç Çubuğunu Göster" : "Araç Çubuğunu Gizle"}
                >
                  {isToolbarHidden ? <FaChevronUp size={16} className="text-gray-600" /> : <FaChevronDown size={16} className="text-gray-600" />}
                </button>
                
                {/* Question Number */}
                <div className={`rounded-full w-8 h-8 flex items-center justify-center border ${
                  glassEffects !== false
                    ? 'bg-white/30 backdrop-blur-md border-white/40'
                    : 'bg-gray-100 border-gray-200'
                }`}>
                  <span className="text-xs font-bold text-gray-800">{currentIndex + 1}</span>
                </div>
              </div>
              
              {/* Center Section - Answer Options */}
              <div className="flex gap-1">
                {(currentQbQuestion.answerType === 1 ? ['A', 'B', 'C', 'D'] : ['A', 'B', 'C', 'D', 'E']).map(choice => {
                  const answer = opticalAnswers[currentQbQuestion.questionId || ''];
                  return (
                    <button
                      key={choice}
                      onClick={() => {
                        if (onAnswerSelect) {
                          onAnswerSelect(choice as 'A' | 'B' | 'C' | 'D' | 'E');
                        }
                      }}
                      className={`${isTablet ? 'w-9 h-9' : 'w-8 h-8'} rounded-full text-xs font-semibold transition-all ${
                        answer === choice
                          ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white scale-105 shadow-lg'
                          : glassEffects !== false
                            ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                      }`}
                    >
                      {choice}
                    </button>
                  );
                })}
              </div>
              
              {/* Right Section - Action Buttons */}
              <div className="flex gap-1">
                {showOpticalForm && questions.length > 0 ? (
                  <button
                    onClick={() => setShowOpticalFormModal(true)}
                    className={`w-8 h-8 rounded-full transition-all border flex items-center justify-center relative ${
                      glassEffects !== false
                        ? 'bg-white/30 backdrop-blur-md hover:bg-white/40 border-white/40'
                        : 'bg-gray-100 hover:bg-gray-200 border-gray-200'
                    }`}
                    title="Tüm Sorular"
                  >
                    <FaList size={16} className="text-gray-700" />
                    {questions.length > 0 && (
                      <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {currentIndex + 1}
                      </div>
                    )}
                  </button>
                ) : questions.length > 0 && (
                  <button
                    onClick={() => setShowQuestionListModal(true)}
                    className={`w-8 h-8 rounded-full transition-all border flex items-center justify-center relative ${
                      glassEffects !== false
                        ? 'bg-white/30 backdrop-blur-md hover:bg-white/40 border-white/40'
                        : 'bg-gray-100 hover:bg-gray-200 border-gray-200'
                    }`}
                    title="Soru Listesi"
                  >
                    <FaList size={16} className="text-gray-700" />
                    {questions.length > 0 && (
                      <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {currentIndex + 1}
                      </div>
                    )}
                  </button>
                )}
                
                {/* Video Button */}
                {hasVideo && onVideoClick && (
                  <button
                    onClick={onVideoClick}
                    className={`w-8 h-8 rounded-full transition-all border flex items-center justify-center ${
                      glassEffects !== false
                        ? 'bg-white/30 backdrop-blur-md hover:bg-white/40 border-white/40'
                        : 'bg-gray-100 hover:bg-gray-200 border-gray-200'
                    }`}
                    title="Çözümü İzle"
                  >
                    <FaVideo size={16} className="text-gray-700" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Main Toolbar */}
        <div className={`flex justify-center gap-1 ${isTablet ? 'px-2' : 'px-1'} py-0.5 ${
          isToolbarHidden && !isLandscape ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}>
          {/* Container - Centered for all devices */}
          <div className="flex items-center gap-1">
            {/* All Tools Group - Centered on tablet */}
            <div className={`flex items-center gap-0.5`}>
            {primaryTools.map(({ tool, icon, label }) => (
              <button
                key={tool}
                onClick={() => handleToolChange(tool)}
                className={`relative ${isTablet ? 'p-2' : 'p-1.5'} rounded-lg transition-all ${
                  currentTool === tool
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg scale-105'
                    : glassEffects !== false
                      ? 'bg-white/40 backdrop-blur-md text-gray-700 hover:bg-white/50 border border-white/40 active:scale-95'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200 active:scale-95'
                }`}
                title={label}
              >
                {React.cloneElement(icon, { size: isTablet ? 18 : 16 })}
              </button>
            ))}
            
            {/* Extended tools for tablets */}
            {isTablet && extendedTools.map(({ tool, icon, label }) => (
              <button
                key={tool}
                onClick={() => {
                  if (tool === 'shapes') {
                    setShowShapeMenu(true);
                  } else {
                    handleToolChange(tool as Tool);
                  }
                }}
                className={`relative ${isTablet ? 'p-2' : 'p-1.5'} rounded-lg transition-all ${
                  tool === 'shapes' && currentShapeTool
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg scale-105'
                    : currentTool === tool
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg scale-105'
                      : glassEffects !== false
                        ? 'bg-white/40 backdrop-blur-md text-gray-700 hover:bg-white/50 border border-white/40 active:scale-95'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200 active:scale-95'
                }`}
                title={label}
              >
                {tool === 'shapes' && currentShapeTool ? (
                  React.cloneElement(currentShapeTool.icon, { 
                    className: 'text-sm' 
                  })
                ) : (
                  React.cloneElement(icon, { size: isTablet ? 18 : 16 })
                )}
              </button>
            ))}
            
            {/* Divider for tablets */}
            {isTablet && <div className="w-px h-6 bg-white/20 mx-1" />}
            
            {/* Quick Colors for tablets */}
            {isTablet && (
              <>
                <button
                  onClick={() => handleColorChange('#000000')}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentColor === '#000000'
                      ? 'bg-gradient-to-r from-gray-700 to-gray-900 shadow-lg'
                      : glassEffects !== false
                        ? 'bg-white/30 backdrop-blur-md hover:bg-white/40 border border-white/40'
                        : 'bg-gray-100 hover:bg-gray-200 border border-gray-200'
                  }`}
                  title="Siyah"
                >
                  <div className="w-4 h-4 rounded-full bg-black" />
                </button>
                <button
                  onClick={() => handleColorChange('#FF0000')}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentColor === '#FF0000'
                      ? 'bg-gradient-to-r from-red-500 to-red-700 shadow-lg'
                      : glassEffects !== false
                        ? 'bg-white/30 backdrop-blur-md hover:bg-white/40 border border-white/40'
                        : 'bg-gray-100 hover:bg-gray-200 border border-gray-200'
                  }`}
                  title="Kırmızı"
                >
                  <div className="w-4 h-4 rounded-full bg-red-500" />
                </button>
                <button
                  onClick={() => handleColorChange('#0000FF')}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentColor === '#0000FF'
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg'
                      : glassEffects !== false
                        ? 'bg-white/30 backdrop-blur-md hover:bg-white/40 border border-white/40'
                        : 'bg-gray-100 hover:bg-gray-200 border border-gray-200'
                  }`}
                  title="Mavi"
                >
                  <div className="w-4 h-4 rounded-full bg-blue-500" />
                </button>
              </>
            )}

            {/* Color & Width Controls */}
            <button
              onClick={() => setShowColorPicker(!showColorPicker)}
              className={`relative ${isTablet ? 'p-2' : 'p-1.5'} rounded-lg border transition-all active:scale-95 ${
                glassEffects !== false
                  ? 'bg-white/40 backdrop-blur-md hover:bg-white/50 border-white/40'
                  : 'bg-gray-100 hover:bg-gray-200 border-gray-200'
              }`}
            >
              <div
                className="w-4 h-4 rounded-full border-2 border-gray-400 shadow-sm"
                style={{ backgroundColor: currentColor }}
              />
            </button>

            {/* Width */}
            <button
              onClick={() => setShowWidthPicker(!showWidthPicker)}
              className={`relative ${isTablet ? 'p-2' : 'p-1.5'} rounded-lg border transition-all active:scale-95 ${
                glassEffects !== false
                  ? 'bg-white/40 backdrop-blur-md hover:bg-white/50 border-white/40'
                  : 'bg-gray-100 hover:bg-gray-200 border-gray-200'
              }`}
            >
              <div className="flex items-center gap-1">
                <div 
                  className="rounded-full bg-gray-700" 
                  style={{ 
                    width: Math.min(18, 6 + currentWidth * 0.5) + 'px',
                    height: Math.min(18, 6 + currentWidth * 0.5) + 'px'
                  }}
                />
                <span className="text-xs font-semibold text-gray-700">{currentWidth}</span>
              </div>
            </button>
            
            {/* Divider */}
            {isTablet && <div className="w-px h-6 bg-white/20 mx-1" />}
            {/* Navigation Buttons */}
            {questions.length > 0 && onNavigatePrevious && onNavigateNext && (isLandscape || (isTablet && showTabletNavigation)) && (
              <>
                <button
                  onClick={onNavigatePrevious}
                  disabled={currentIndex === 0}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentIndex === 0 
                      ? 'text-gray-300 cursor-not-allowed bg-white/10' 
                      : glassEffects !== false
                        ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                  }`}
                  title="Önceki Soru"
                >
                  <FaChevronLeft size={18} />
                </button>
                <button
                  onClick={onNavigateNext}
                  disabled={currentIndex >= questions.length - 1}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentIndex >= questions.length - 1 
                      ? 'text-gray-300 cursor-not-allowed bg-white/10' 
                      : glassEffects !== false
                        ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                  }`}
                  title="Sonraki Soru"
                >
                  <FaChevronRight size={18} />
                </button>
              </>
            )}
            

            {/* Recording Controls for Tablet */}
            {isTablet && effectivePermissions.canRecord && !recordingSession.isRecording && (
              <button
                onClick={handleStartRecording}
                className={`relative ${isTablet ? 'p-2' : 'p-1.5'} rounded-lg border transition-all active:scale-95 ${
                  glassEffects !== false
                    ? 'bg-white/40 backdrop-blur-md hover:bg-white/50 border-white/40'
                    : 'bg-gray-100 hover:bg-gray-200 border-gray-200'
                }`}
                title="Kayıt Başlat"
              >
                <FaMicrophone size={isTablet ? 18 : 16} className="text-red-500" />
              </button>
            )}

            {/* Recording Button - Always visible when recording */}
            {effectivePermissions.canRecord && recordingSession.isRecording && (
              <button
                onClick={handleStopRecording}
                className="p-1.5 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white animate-pulse shadow-lg"
                title="Kaydı Durdur"
              >
                <FaStop size={18} />
              </button>
            )}

            {/* Recordings List for Tablet */}
            {isTablet && effectivePermissions.canViewRecordings && (
              <button
                onClick={async () => {
                  setShowRecordings(true);
                  // Load recordings logic
                  const currentQuestionId = currentQbQuestion?.questionId;
                  if (currentQuestionId) {
                    await loadRecordingsForQuestion(currentQuestionId);
                  }
                }}
                className={`relative ${isTablet ? 'p-2' : 'p-1.5'} rounded-lg border transition-all active:scale-95 ${
                  glassEffects !== false
                    ? 'bg-white/40 backdrop-blur-md hover:bg-white/50 border-white/40'
                    : 'bg-gray-100 hover:bg-gray-200 border-gray-200'
                }`}
                title="Kayıtlar"
              >
                <FaList size={isTablet ? 18 : 16} className="text-purple-500" />
                {recordingsCount > 0 && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-xs text-white font-bold">{recordingsCount}</span>
                  </div>
                )}
              </button>
            )}

            {/* Question List Button - Show when not in optical mode */}
            {!showOpticalForm && questions && questions.length > 0 && (
              <button
                onClick={() => setShowQuestionListModal(true)}
                className={`relative ${isTablet ? 'p-2' : 'p-1.5'} rounded-lg border transition-all active:scale-95 ${
                  glassEffects !== false
                    ? 'bg-white/40 backdrop-blur-md hover:bg-white/50 border-white/40'
                    : 'bg-gray-100 hover:bg-gray-200 border-gray-200'
                }`}
                title="Soru Listesi"
              >
                <FaList size={isTablet ? 18 : 16} className="text-blue-500" />
                {currentIndex >= 0 && (
                  <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {currentIndex + 1}
                  </div>
                )}
              </button>
            )}

            {/* More */}
            <button
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className={`relative ${isTablet ? 'p-2' : 'p-1.5'} rounded-lg border transition-all active:scale-95 ${
                glassEffects !== false
                  ? 'bg-white/40 backdrop-blur-md hover:bg-white/50 border-white/40 text-gray-700'
                  : 'bg-gray-100 hover:bg-gray-200 border-gray-200 text-gray-700'
              }`}
              title="Daha Fazla"
            >
              <FaEllipsisH size={isTablet ? 18 : 16} />
            </button>
          </div>{/* End of All Tools Group */}
          </div>{/* End of Container */}
        </div>{/* End of Main Toolbar */}
      </div>{/* End of Combined Toolbar and Optical Form */}

      {/* Color Picker Popup */}
      {showColorPicker && (
        <>
          {/* Backdrop for mobile */}
          <div className="fixed inset-0 z-40" onClick={() => setShowColorPicker(false)} />
          <div className={`fixed z-50 bottom-16 left-1/2 transform -translate-x-1/2 rounded-xl shadow-xl p-2 border ${
            glassEffects !== false 
              ? getGlassClasses(devicePreferences?.preferences?.glassColor || 'crystal', isDark)
              : 'bg-white border-gray-200'
          }`}>
          {/* Quick Colors */}
          <div className="flex gap-1 mb-1">
            {quickColors.map((color, index) => (
              <button
                key={index}
                onClick={() => {
                  handleColorChange(color);
                  setShowColorPicker(false);
                }}
                className={`w-8 h-8 rounded-lg border-2 transition-all ${
                  currentColor === color ? 'border-blue-500 scale-110' : 'border-gray-200'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          
          {/* All Colors */}
          <div className="grid grid-cols-5 gap-1 mb-2">
            {colors.map((color) => (
              <button
                key={color}
                onClick={() => {
                  handleColorChange(color);
                  setShowColorPicker(false);
                }}
                className={`w-8 h-8 rounded-lg border-2 transition-all ${
                  currentColor === color ? 'border-blue-500 scale-110' : 'border-gray-200'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          
          <input
            type="color"
            value={currentColor}
            onChange={(e) => handleColorChange(e.target.value)}
            className="w-full h-8 rounded cursor-pointer"
          />
        </div>
        </>
      )}

      {/* Width Picker Popup */}
      {showWidthPicker && (
        <>
          {/* Backdrop for mobile */}
          <div className="fixed inset-0 z-40" onClick={() => setShowWidthPicker(false)} />
          <div className={`fixed z-50 bottom-16 left-1/2 transform -translate-x-1/2 rounded-xl shadow-xl p-2 border max-w-xs ${
            glassEffects !== false 
              ? getGlassClasses(devicePreferences?.preferences?.glassColor || 'crystal', isDark)
              : 'bg-white border-gray-200'
          }`}>
          <div className="grid grid-cols-6 gap-2">
            {widths.map((width) => (
              <button
                key={width}
                onClick={() => {
                  handleWidthChange(width);
                  setShowWidthPicker(false);
                }}
                className={`p-1.5 rounded-lg transition-all ${
                  currentWidth === width ? 'bg-blue-500 text-white' : 'bg-gray-100'
                }`}
              >
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={`rounded-full ${currentWidth === width ? 'bg-white' : 'bg-gray-700'}`}
                    style={{ 
                      width: Math.min(20, 4 + width * 0.8) + 'px',
                      height: Math.min(20, 4 + width * 0.8) + 'px'
                    }}
                  />
                  <span className="text-[10px]">{width}</span>
                </div>
              </button>
            ))}
          </div>
          </div>
        </>
      )}

      {/* Shape Menu */}
      {showShapeMenu && (
        <>
          {/* Backdrop for closing menu */}
          <div 
            className="fixed inset-0 z-[59]" 
            onClick={() => setShowShapeMenu(false)}
          />
          
          {/* Shape dropdown menu - positioned relative to shape button */}
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-[60] mb-2">
            <div className={`${
              glassEffects !== false 
                ? 'bg-white/95 backdrop-blur-xl border border-white/40' 
                : 'bg-white border border-gray-200'
            } rounded-2xl shadow-2xl overflow-hidden w-[240px]`}>
              
              {/* All shapes visible */}
              <div className="grid grid-cols-4 gap-1 p-2">
                {[
                  { tool: 'circle', icon: <BsCircle size={20} />, label: 'Daire' },
                  { tool: 'rectangle', icon: <BsSquare size={20} />, label: 'Dikdörtgen' },
                  { tool: 'triangle', icon: <BsTriangle size={20} />, label: 'Üçgen' },
                  { tool: 'line', icon: <FaMinus size={20} />, label: 'Çizgi' },
                  { tool: 'arrow', icon: <FaArrowRight size={20} />, label: 'Ok' },
                  { tool: 'star', icon: <FaStar size={20} />, label: 'Yıldız' },
                  { tool: 'pentagon', icon: <BsPentagon size={20} />, label: 'Beşgen' },
                  { tool: 'hexagon', icon: <BsHexagon size={20} />, label: 'Altıgen' },
                  { tool: 'ellipse', icon: <div className="w-5 h-3 border-2 border-current rounded-full" />, label: 'Elips' },
                  { tool: 'diamond', icon: <BsDiamond size={20} />, label: 'Elmas' },
                  { tool: 'coordinate', icon: <VscGraphLine size={20} />, label: 'Koordinat' },
                  { tool: 'grid', icon: <BiGridAlt size={20} />, label: 'Izgara' },
                  { tool: 'cube', icon: <FaCube size={20} />, label: 'Küp' },
                  { tool: 'cylinder', icon: <TbCylinder size={20} />, label: 'Silindir' },
                  { tool: 'pyramid', icon: <TbPyramid size={20} />, label: 'Piramit' },
                ].map(({ tool, icon, label }) => (
                  <button
                    key={tool}
                    onClick={() => {
                      handleToolChange(tool as Tool);
                      setShowShapeMenu(false);
                    }}
                    className={`aspect-square p-1.5 rounded-lg transition-all flex items-center justify-center ${
                      currentTool === tool
                        ? 'bg-blue-500 text-white shadow-sm'
                        : glassEffects !== false
                          ? 'hover:bg-white/60 text-gray-700'
                          : 'hover:bg-gray-100 text-gray-700'
                    }`}
                    title={label}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}


      {/* More Menu with Glassmorphism */}
      {showMoreMenu && (
        <>
          {/* Backdrop for mobile */}
          <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
          <div className={`fixed z-50 bottom-16 right-2 rounded-xl shadow-2xl p-3 border min-w-[200px] ${
            glassEffects !== false 
              ? getGlassClasses(devicePreferences?.preferences?.glassColor || 'crystal', isDark)
              : 'bg-white border-gray-200'
          }`}>
          <div className="space-y-1">
            {/* Recording Section */}
            {(effectivePermissions.canRecord || effectivePermissions.canViewRecordings) && (
              <div className="border-b border-white/20 pb-2 mb-2">
              {effectivePermissions.canRecord && recordingSession.isRecording ? (
                <button
                  onClick={() => {
                    handleStopRecording();
                    setShowMoreMenu(false);
                  }}
                  className="w-full p-2 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg flex items-center gap-2 animate-pulse"
                >
                  <FaStop size={20} />
                  <span className="text-sm font-medium">Kaydı Durdur</span>
                </button>
              ) : effectivePermissions.canRecord ? (
                <button
                  onClick={() => {
                    handleStartRecording();
                    setShowMoreMenu(false);
                  }}
                  className={`w-full p-2 rounded-lg flex items-center gap-2 ${
                    glassEffects !== false
                      ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                  }`}
                >
                  <FaMicrophone size={20} />
                  <span className="text-sm">Kayıt Başlat</span>
                </button>
              ) : null}
              
              {effectivePermissions.canViewRecordings && (
                <button
                onClick={async () => {
                  setShowRecordings(true);
                  setShowMoreMenu(false);
                  
                  // Load recordings for current question
                  const currentQuestionId = currentQbQuestion?.questionId;
                  if (currentQuestionId) {
                    try {
                      setLoadingRecordings(true);
                      
                      // Get user info
                      const { user } = useUserStore.getState();
                      const { currentTenant } = useTenantStore.getState();
                      
                      // Load recordings based on effective permissions
                      let recordings: RecordingMetadata[] = [];
                      const recordingIds = new Set<string>();
                      
                      // Load own recordings if permitted
                      if (effectivePermissions.recordingVisibility?.showOwnRecordings && user?.id) {
                        const ownRecordings = await RecordingService.getRecordingsByQuestion(
                          currentQuestionId,
                          user.id.toString(),
                          50
                        );
                        ownRecordings.forEach(r => {
                          recordings.push(r);
                          recordingIds.add(r.id);
                        });
                      }
                      
                      // Load public recordings if permitted
                      if (effectivePermissions.recordingVisibility?.showPublicRecordings) {
                        const publicRecordings = await RecordingService.getPublicRecordingsByQuestion(
                          currentQuestionId,
                          50
                        );
                        publicRecordings.forEach(r => {
                          if (!recordingIds.has(r.id)) {
                            recordings.push(r);
                            recordingIds.add(r.id);
                          }
                        });
                      }
                      
                      // Load tenant recordings if permitted
                      if (effectivePermissions.recordingVisibility?.showTenantRecordings && currentTenant?.tenantId) {
                        const tenantRecordings = await RecordingService.getTenantRecordingsByQuestion(
                          currentQuestionId,
                          currentTenant.tenantId.toString(),
                          50
                        );
                        tenantRecordings.forEach(r => {
                          if (!recordingIds.has(r.id)) {
                            recordings.push(r);
                            recordingIds.add(r.id);
                          }
                        });
                      }
                      
                      // Sort by creation date (newest first)
                      recordings.sort((a, b) => {
                        const dateA = a.createdAt instanceof Date ? a.createdAt.getTime() : a.createdAt;
                        const dateB = b.createdAt instanceof Date ? b.createdAt.getTime() : b.createdAt;
                        return dateB - dateA;
                      });
                      
                      // Update the state with filtered recordings
                      setFirebaseRecordings(recordings);
                    } catch (error) {
                      console.error('Error loading recordings:', error);
                    } finally {
                      setLoadingRecordings(false);
                    }
                  }
                }}
                className={`w-full p-2 rounded-lg flex items-center gap-2 mt-1 relative ${
                  glassEffects !== false
                    ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                <FaList size={20} />
                <span className="text-sm">Kayıtlar</span>
                {recordingsCount > 0 && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-xs text-white font-bold">{recordingsCount}</span>
                  </div>
                )}
              </button>
              )}
              </div>
            )}

            {/* Erase/Clear Section */}
            <div className="border-b border-white/20 pb-2 mb-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 px-1">Temizleme</p>
              
              <button
                onClick={() => {
                  handleToolChange('eraser');
                  setShowMoreMenu(false);
                }}
                className={`w-full p-2 rounded-lg flex items-center gap-2 mt-1 ${
                  currentTool === 'eraser'
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg'
                    : glassEffects !== false
                      ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                <FaEraser size={20} />
                <span className="text-sm">Silgi</span>
              </button>

              <button
                onClick={() => {
                  handleToolChange('area-eraser');
                  setShowMoreMenu(false);
                }}
                className={`w-full p-2 rounded-lg flex items-center gap-2 mt-1 ${
                  currentTool === 'area-eraser'
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg'
                    : glassEffects !== false
                      ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                <FaVectorSquare size={20} />
                <span className="text-sm">Bölge Sil</span>
              </button>

              <button
                onClick={() => {
                  clearCanvas();
                  setShowMoreMenu(false);
                }}
                className={`w-full p-2 rounded-lg flex items-center gap-2 mt-1 ${
                  glassEffects !== false
                    ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                <FaTrash size={20} className="text-red-500" />
                <span className="text-sm">Tümünü Temizle</span>
              </button>
            </div>

            {allowExport && (
              <ExportButton
                canvas={canvasRef}
                isEnabled={true}
                showText={true}
                className={`w-full p-3 rounded-lg flex items-center justify-start gap-3 ${
                  glassEffects !== false
                    ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              />
            )}

            <button
              onClick={() => handleToolChange('laser')}
              className={`w-full p-3 rounded-lg flex items-center gap-3 ${
                currentTool === 'laser'
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg'
                  : glassEffects !== false
                    ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              <FaEye size={20} />
              <span className="text-sm">Lazer</span>
            </button>

            {/* Elimination Display Toggle */}
            {hasEliminatedChoices && onToggleEliminationDisplay && (
              <button
                onClick={() => {
                  onToggleEliminationDisplay();
                  setShowMoreMenu(false);
                }}
                className={`w-full p-3 rounded-lg flex items-center gap-3 ${
                  showEliminationDisplay
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg'
                    : glassEffects !== false
                      ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                <FaTimes size={20} />
                <span className="text-sm">{showEliminationDisplay ? "Elenenleri Gizle" : "Elenenleri Göster"}</span>
              </button>
            )}

            {/* Solution Viewer for mobile
            {allowSolutionViewing && currentQbQuestion?.questionId && (
              <div className="w-full">
                <SolutionViewerDropdown
                  questionId={currentQbQuestion.questionId}
                  currentTestId={currentTestId}
                  config={{
                    allowSolutionViewing,
                    visibilityMode: solutionVisibilityMode,
                    allowOwnSolutionManagement,
                    maxSolutionsToShow,
                    showAuthorInfo: showSolutionAuthorInfo,
                    showSolutionStats
                  }}
                  className="w-full"
                />
              </div>
            )} */}

            {/* Video Button - Always show when available */}
            {hasVideo && onVideoClick && (
              <button
                onClick={() => {
                  onVideoClick();
                  setShowMoreMenu(false);
                }}
                className={`w-full p-3 rounded-lg flex items-center gap-3 ${
                  glassEffects !== false
                    ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                <FaVideo size={20} />
                <span className="text-sm">Video İzle</span>
              </button>
            )}


            <button
              onClick={() => {
                setShowSettings(true);
                setShowMoreMenu(false);
              }}
              className={`w-full p-3 rounded-lg flex items-center gap-3 ${
                glassEffects !== false
                  ? 'bg-white/30 backdrop-blur-md text-gray-700 hover:bg-white/40 border border-white/40'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              <FaCog size={20} />
              <span className="text-sm">Ayarlar</span>
            </button>
          </div>
          </div>
        </>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[60] flex items-end bg-black/50 backdrop-enter" onClick={() => setShowSettings(false)}>
          <div className={`${
            glassEffects !== false 
              ? 'bg-white/95 backdrop-blur-xl border-t border-white/40' 
              : 'bg-white border-t border-gray-200'
          } rounded-t-2xl w-full max-h-[90vh] overflow-hidden flex flex-col modal-slide-up`} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-4 flex-shrink-0 ${
              glassEffects !== false 
                ? 'border-b border-white/40' 
                : 'border-b border-gray-200'
            }`}>
              <h3 className="text-lg font-semibold text-gray-800">Ayarlar</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2.5 rounded-lg hover:bg-gray-100/50 transition-colors"
                aria-label="Kapat"
              >
                <FaTimes size={20} className="text-gray-600" />
              </button>
            </div>
            
            <div className="p-4 space-y-4 overflow-y-auto flex-1 -webkit-overflow-scrolling-touch">
              {/* Device Info */}
              <div className={`p-4 rounded-xl ${
                glassEffects !== false 
                  ? 'bg-blue-50/60 backdrop-blur-sm border border-blue-100/40' 
                  : 'bg-blue-50 border border-blue-100'
              }`}>
                <h4 className="font-medium text-blue-900 mb-3">Cihaz Bilgileri</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cihaz ID:</span>
                    <span className="font-mono text-xs text-gray-800">{generateDeviceId().slice(-12)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Platform:</span>
                    <span className="font-medium text-gray-800">{navigator.platform}</span>
                  </div>
                </div>
              </div>

              {/* Pen Mode */}
              <div className={`flex items-center justify-between p-4 rounded-xl ${
                glassEffects !== false 
                  ? 'bg-white/50 backdrop-blur-sm border border-gray-200/40' 
                  : 'bg-gray-50 border border-gray-200'
              }`}>
                <div>
                  <p className="font-medium text-gray-800">Kalem Modu</p>
                  <p className="text-sm text-gray-600">Sadece kalem girişi</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPenMode}
                    onChange={(e) => handlePenModeChange(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Touch Enabled */}
              <div className={`flex items-center justify-between p-4 rounded-xl ${
                glassEffects !== false 
                  ? 'bg-white/50 backdrop-blur-sm border border-gray-200/40' 
                  : 'bg-gray-50 border border-gray-200'
              }`}>
                <div>
                  <p className="font-medium text-gray-800">Dokunma Desteği</p>
                  <p className="text-sm text-gray-600">Parmakla çizim</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={touchEnabled}
                    onChange={(e) => handleTouchEnabledChange(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Tablet Navigation - Only show on tablets */}
              {isTablet && (
                <div className={`flex items-center justify-between p-4 rounded-xl ${
                  glassEffects !== false 
                    ? 'bg-white/50 backdrop-blur-sm border border-gray-200/40' 
                    : 'bg-gray-50 border border-gray-200'
                }`}>
                  <div>
                    <p className="font-medium text-gray-800">Tablet Navigasyon</p>
                    <p className="text-sm text-gray-600">Önceki/Sonraki soru butonları</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showTabletNavigation}
                      onChange={(e) => setShowTabletNavigation(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              )}

              {/* Shape Recognition */}
              <div className={`flex items-center justify-between p-4 rounded-xl ${
                glassEffects !== false 
                  ? 'bg-white/50 backdrop-blur-sm border border-gray-200/40' 
                  : 'bg-gray-50 border border-gray-200'
              }`}>
                <div>
                  <p className="font-medium text-gray-800">Şekil Tanıma</p>
                  <p className="text-sm text-gray-600">Otomatik düzeltme</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shapeRecognitionEnabled}
                    onChange={(e) => handleShapeRecognitionChange(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Answer Feedback - Only show when optical form is enabled */}
              {showOpticalForm && (
                <div className={`flex items-center justify-between p-4 rounded-xl ${
                  glassEffects !== false 
                    ? 'bg-white/50 backdrop-blur-sm border border-gray-200/40' 
                    : 'bg-gray-50 border border-gray-200'
                }`}>
                  <div>
                    <p className="font-medium text-gray-800">Cevap Kontrolü</p>
                    <p className="text-sm text-gray-600">Doğru/yanlış işaretlerini göster</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showAnswerFeedback}
                      onChange={(e) => handleShowAnswerFeedbackChange(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              )}

              {/* Pen Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-800">Kalem Ayarları</h4>
                
                <div className={`p-4 rounded-xl ${
                  glassEffects !== false 
                    ? 'bg-white/50 backdrop-blur-sm border border-gray-200/40' 
                    : 'bg-gray-50 border border-gray-200'
                }`}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hassasiyet: <span className="text-blue-600">{penSettings.sensitivity.toFixed(1)}</span>
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="2"
                    step="0.1"
                    value={penSettings.sensitivity}
                    onChange={(e) => handlePenSettingsChange({ sensitivity: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                <div className={`p-4 rounded-xl ${
                  glassEffects !== false 
                    ? 'bg-white/50 backdrop-blur-sm border border-gray-200/40' 
                    : 'bg-gray-50 border border-gray-200'
                }`}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Yumuşatma: <span className="text-blue-600">{penSettings.smoothing.toFixed(1)}</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={penSettings.smoothing}
                    onChange={(e) => handlePenSettingsChange({ smoothing: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                <div className={`flex items-center justify-between p-4 rounded-xl ${
                  glassEffects !== false 
                    ? 'bg-white/50 backdrop-blur-sm border border-gray-200/40' 
                    : 'bg-gray-50 border border-gray-200'
                }`}>
                  <label className="text-sm font-medium text-gray-700">Basınç Hassasiyeti</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={penSettings.pressureSensitivity}
                      onChange={(e) => handlePenSettingsChange({ pressureSensitivity: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              {/* Glass Effects */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-800">Görünüm</h4>
                
                <div className={`flex items-center justify-between p-4 rounded-xl ${
                  glassEffects !== false 
                    ? 'bg-white/50 backdrop-blur-sm border border-gray-200/40' 
                    : 'bg-gray-50 border border-gray-200'
                }`}>
                  <div>
                    <p className="font-medium text-gray-800">Cam Efektleri</p>
                    <p className="text-sm text-gray-600">Şeffaf arka plan efektleri</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={glassEffects !== false}
                      onChange={(e) => handleGlassEffectsChange(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* Glass Color Picker - Only show when glass effects are enabled */}
                {glassEffects !== false && (
                  <div className="p-4 rounded-xl bg-white/50 backdrop-blur-sm border border-gray-200/40">
                    <p className="font-medium text-gray-800 mb-3">Cam Rengi</p>
                    <div className="grid grid-cols-5 gap-2">
                      <button
                        onClick={() => handleGlassColorChange('crystal')}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          (devicePreferences?.preferences?.glassColor || 'crystal') === 'crystal'
                            ? 'border-blue-500 shadow-lg'
                            : 'border-transparent'
                        }`}
                        title="Kristal"
                      >
                        <div className="w-full h-8 rounded bg-gradient-to-br from-white/80 to-gray-200/80" />
                      </button>
                      <button
                        onClick={() => handleGlassColorChange('ocean')}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          devicePreferences?.preferences?.glassColor === 'ocean'
                            ? 'border-blue-500 shadow-lg'
                            : 'border-transparent'
                        }`}
                        title="Okyanus"
                      >
                        <div className="w-full h-8 rounded bg-gradient-to-br from-blue-400/80 to-blue-600/80" />
                      </button>
                      <button
                        onClick={() => handleGlassColorChange('sunset')}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          devicePreferences?.preferences?.glassColor === 'sunset'
                            ? 'border-blue-500 shadow-lg'
                            : 'border-transparent'
                        }`}
                        title="Gün Batımı"
                      >
                        <div className="w-full h-8 rounded bg-gradient-to-br from-orange-400/80 to-red-500/80" />
                      </button>
                      <button
                        onClick={() => handleGlassColorChange('forest')}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          devicePreferences?.preferences?.glassColor === 'forest'
                            ? 'border-blue-500 shadow-lg'
                            : 'border-transparent'
                        }`}
                        title="Orman"
                      >
                        <div className="w-full h-8 rounded bg-gradient-to-br from-green-400/80 to-green-600/80" />
                      </button>
                      <button
                        onClick={() => handleGlassColorChange('royal')}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          devicePreferences?.preferences?.glassColor === 'royal'
                            ? 'border-blue-500 shadow-lg'
                            : 'border-transparent'
                        }`}
                        title="Kraliyet"
                      >
                        <div className="w-full h-8 rounded bg-gradient-to-br from-purple-400/80 to-purple-600/80" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Question Frame Margins */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-800">Soru Çerçeve Kenar Boşlukları</h4>
                <p className="text-sm text-gray-600">Soruların ekran kenarlarından uzaklığını ayarlayın</p>
                
                <div className={`p-4 rounded-xl space-y-3 ${
                  glassEffects !== false 
                    ? 'bg-white/50 backdrop-blur-sm border border-gray-200/40' 
                    : 'bg-gray-50 border border-gray-200'
                }`}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Üst Boşluk: <span className="text-blue-600">{devicePreferences?.preferences?.questionFrameMargins?.top || 50}px</span>
                    </label>
                    <input
                      type="range"
                      min="25"
                      max="100"
                      value={devicePreferences?.preferences?.questionFrameMargins?.top || 50}
                      onChange={(e) => handleQuestionFrameMarginsChange({ top: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alt Boşluk: <span className="text-blue-600">{devicePreferences?.preferences?.questionFrameMargins?.bottom || 250}px</span>
                    </label>
                    <input
                      type="range"
                      min="50"
                      max="500"
                      value={devicePreferences?.preferences?.questionFrameMargins?.bottom || 250}
                      onChange={(e) => handleQuestionFrameMarginsChange({ bottom: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sol Boşluk: <span className="text-blue-600">{devicePreferences?.preferences?.questionFrameMargins?.left || 30}px</span>
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={devicePreferences?.preferences?.questionFrameMargins?.left || 30}
                      onChange={(e) => handleQuestionFrameMarginsChange({ left: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sağ Boşluk: <span className="text-blue-600">{devicePreferences?.preferences?.questionFrameMargins?.right || 30}px</span>
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={devicePreferences?.preferences?.questionFrameMargins?.right || 30}
                      onChange={(e) => handleQuestionFrameMarginsChange({ right: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <button
                    onClick={() => handleQuestionFrameMarginsChange({ top: 50, bottom: 250, left: 30, right: 30 })}
                    className="w-full mt-3 py-2 px-4 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm text-gray-700"
                  >
                    Varsayılan Değerlere Dön
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Recording Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onClick={() => setShowSaveDialog(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4 text-center">Kayıt Tamamlandı!</h3>
            <p className="text-center mb-4 text-gray-600">
              Kayıtınıza bir isim verin
            </p>
            
            <input
              type="text"
              value={recordingName}
              onChange={(e) => setRecordingName(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 bg-white text-gray-900 border-gray-200 focus:border-blue-500 focus:outline-none placeholder-gray-400"
              placeholder="Kayıt adı..."
              autoFocus
            />
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="flex-1 py-2 px-4 bg-gray-200 rounded-xl hover:bg-gray-300"
              >
                İptal
              </button>
              <button
                onClick={handleSaveRecording}
                className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-xl hover:bg-blue-600 flex items-center justify-center gap-2"
              >
                <FaSave />
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recordings List Modal */}
      {showRecordings && (
        <div className="fixed inset-0 z-[60] flex items-end bg-black/50 backdrop-enter" onClick={() => setShowRecordings(false)}>
          <div className="bg-white rounded-t-2xl w-full max-h-[80vh] overflow-hidden flex flex-col modal-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold">
                  {currentQbQuestion?.questionNumber ? `Soru ${currentQbQuestion.questionNumber} - ` : ''}
                  Kayıtlar
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {firebaseRecordings.length} kayıt bulundu
                  {firebaseRecordings.some(r => r.solutionMetadata?.isPublic) && ' (genel kayıtlar dahil)'}
                </p>
              </div>
              <button
                onClick={() => setShowRecordings(false)}
                className="p-2.5 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Kapat"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-3 overflow-y-auto flex-1 -webkit-overflow-scrolling-touch">
              {loadingRecordings ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-500">Kayıtlar yükleniyor...</p>
                </div>
              ) : firebaseRecordings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FaCircle className="text-4xl mx-auto mb-4 opacity-50" />
                  <p>Henüz kayıt bulunmuyor</p>
                  <p className="text-sm">İlk kayıtınızı oluşturun!</p>
                </div>
              ) : (
                firebaseRecordings.map((recording) => (
                  <div 
                    key={recording.id} 
                    className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold mb-1">
                          {recording.solutionMetadata?.solutionName || 
                          `Çözüm ${recording.solutionMetadata?.solutionNumber || 1}`}
                        </h4>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>
                            {recording.authorMetadata.name}
                            {recording.solutionMetadata?.isPublic && ' (Genel)'}
                          </span>
                          <span>{new Date(recording.createdAt).toLocaleDateString('tr-TR')}</span>
                          {recording.duration && <span>{formatDuration(recording.duration * 1000)}</span>}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setShowRecordings(false);
                            setTimeout(() => {
                              navigate(`/player/${recording.id}`);
                            }, 100);
                          }}
                          className="p-2 rounded-lg bg-green-500 text-white hover:bg-green-600"
                          title="Oynat"
                        >
                          <FaPlay className="text-sm" />
                        </button>
                        <button
                          onClick={() => handleShareRecording({ id: recording.id } as Recording)}
                          className="p-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                          title="Paylaş"
                        >
                          <FaShare className="text-sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Optical Form Modal - Using new MobileOpticalForm component */}
      {showOpticalFormModal && showOpticalForm && (
        <MobileOpticalForm
          isOpen={showOpticalFormModal}
          onClose={() => setShowOpticalFormModal(false)}
          questions={questions || []}
          qbGroups={qbGroups || []}
          currentIndex={currentIndex || 0}
          opticalAnswers={Object.keys(opticalAnswers || {}).reduce((acc, key) => {
            const answer = opticalAnswers[key];
            if (answer !== null && answer !== undefined) {
              acc[key] = answer as OpticalAnswer;
            }
            return acc;
          }, {} as Record<string, OpticalAnswer>)}
          onAnswerSelect={(answer: 'A' | 'B' | 'C' | 'D' | 'E' | '', questionId?: string) => {
            if (onAnswerSelect) {
              onAnswerSelect(answer as 'A' | 'B' | 'C' | 'D' | 'E', questionId);
            }
          }}
          onQuestionChange={onQuestionChange || (() => {})}
          showAnswerFeedback={showAnswerFeedback}
          isTestMode={isTestMode}
          totalTime={totalTime}
          isTestCompleted={isTestCompleted}
          onTestComplete={onTestComplete}
        />
      )}

      {/* Compact Question List Modal */}
      {showQuestionListModal && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className={`${
            glassEffects !== false 
              ? 'bg-white/95 backdrop-blur-xl border border-white/40' 
              : 'bg-white border border-gray-200'
          } rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden`}>
            
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Soru Listesi</h2>
              <button
                onClick={() => setShowQuestionListModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <FaTimes className="text-gray-500" />
              </button>
            </div>

            {/* Question Grid */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {qbGroups && qbGroups.length > 0 ? (
                <div className="space-y-4">
                  {qbGroups.map((group, groupIndex) => (
                    <div key={group.groupId} className="space-y-2">
                      {/* Group Name */}
                      <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                        {group.groupName || `Grup ${groupIndex + 1}`}
                      </h3>
                      
                      {/* Courses */}
                      {group.courses?.map((course, courseIndex) => (
                        <div key={course.courseId} className="space-y-2">
                          <h4 className="text-xs font-medium text-gray-500 pl-2">
                            {course.courseName || `Ders ${courseIndex + 1}`}
                          </h4>
                          
                          {/* Questions Grid */}
                          <div className="grid grid-cols-4 gap-2 pl-4">
                            {course.questions?.map((question, questionIndex) => {
                              const isCurrentQuestion = currentQbQuestion?.questionId === question.questionId;
                              const questionNumber = question.questionOrder || question.questionNumber || questionIndex + 1;
                              
                              return (
                                <button
                                  key={question.questionId}
                                  onClick={() => {
                                    if (onQuestionChange) {
                                      // Find the global index of this question
                                      let globalIndex = 0;
                                      let found = false;
                                      
                                      for (const g of qbGroups) {
                                        for (const c of g.courses || []) {
                                          for (const q of c.questions || []) {
                                            if (q.questionId === question.questionId) {
                                              found = true;
                                              break;
                                            }
                                            globalIndex++;
                                          }
                                          if (found) break;
                                        }
                                        if (found) break;
                                      }
                                      
                                      onQuestionChange(globalIndex);
                                      setShowQuestionListModal(false);
                                    }
                                  }}
                                  className={`aspect-square rounded-lg border-2 text-sm font-semibold transition-all ${
                                    isCurrentQuestion
                                      ? 'bg-blue-500 text-white border-blue-600 shadow-lg scale-105'
                                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 hover:border-gray-300 active:scale-95'
                                  }`}
                                >
                                  {questionNumber}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FaList className="text-3xl mx-auto mb-2 opacity-50" />
                  <p>Soru bulunamadı</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    
    </>
  );
};

export default MobileToolbar;